import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Radio, Gamepad2, Sparkles, Coins, ShieldCheck, MessageSquare,
  Users, Rocket, Mail, Zap, Trophy, Clock, Lock, X, Loader2,
  BarChart3, Settings2, ReceiptText, Eye,
  DollarSign, TrendingUp,
} from "lucide-react";
import { grantAdminUnlock, grantDemoUnlock } from "@/lib/prelaunch";
import { LogoMark } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Coming Soon — pre-launch splash for www.mysticast.tv.
 *
 * Purpose:
 *   1. Satisfy Stripe's "business website" verification requirement (visible
 *      business name, description, product overview, contact, legal links).
 *   2. Prevent public signups / purchases / tips until public launch.
 *
 * DO NOT add signup / login / purchase CTAs to this page.
 * DO NOT display a specific launch date — only "Coming Soon" messaging.
 */

export default function ComingSoon() {
  const [staffOpen, setStaffOpen] = useState(false);

  // Keyboard shortcut: Ctrl/Cmd + Shift + L opens the staff login.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "L" || e.key === "l")) {
        e.preventDefault();
        setStaffOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0A0A0C]" data-testid="coming-soon-root">
      {/* Ambient neon orbs + grid */}
      <div className="neon-orb" style={{ background: "#FF1053", width: 340, height: 340, top: -80, left: -80 }} />
      <div className="neon-orb" style={{ background: "#CCFF00", width: 280, height: 280, top: 200, right: -60, animationDelay: "-3s" }} />
      <div className="neon-orb" style={{ background: "#00F0FF", width: 220, height: 220, bottom: 40, left: "40%", animationDelay: "-6s" }} />
      <div className="cyber-grid" />
      <div className="crt-scanlines" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        {/* Status chips */}
        <div className="flex items-center gap-2.5 mb-10 flex-wrap">
          <span className="chip-neon pink flicker-slow" data-testid="cs-status-chip">
            <Clock className="w-3 h-3" /> Pre-Launch
          </span>
          <span className="chip-neon orange">
            <ShieldCheck className="w-3 h-3" /> 18+ Only
          </span>
          <span className="chip-neon cyan">
            <Radio className="w-3 h-3" /> Streaming + Games
          </span>
        </div>

        {/* Hero */}
        <div className="mb-16">
          <div className="sc-label mb-4" style={{ color: "var(--sc-orange)" }}>
            ◢ COMING SOON
          </div>
          <h1 className="pixel-title text-[clamp(2.75rem,9vw,7rem)] leading-none" data-testid="cs-hero-title">
            <span className="glitch" style={{ color: "var(--sc-pink)" }}>MYSTI</span><span className="glitch" style={{ color: "var(--sc-volt)" }}>CAST</span>
            <br />
            <span style={{ color: "var(--sc-volt)" }} className="text-glow-volt">IS ALMOST</span>{" "}
            <span style={{ color: "var(--sc-cyan)" }} className="text-glow-cyan">HERE.</span>
          </h1>

          <p className="text-zinc-300 text-lg max-w-3xl mt-8 leading-relaxed" data-testid="cs-business-description">
            <span className="font-bold text-white">MystiCast</span> is a next-generation live-streaming and multiplayer-gaming platform for creators aged 18+.
            Streamers broadcast from their browser or OBS, host playable in-stream mini-games, and receive tips
            &amp; monthly subscriptions from their community — with real payouts to their bank via Stripe Connect.
            <span className="block mt-3 text-zinc-400 text-base">
              We&apos;re putting the finishing touches on trust &amp; safety, payment compliance, and creator tools before opening the doors.
            </span>
          </p>
        </div>

        {/* What we're building */}
        <div className="mb-16" data-testid="cs-features">
          <div className="sc-label mb-4" style={{ color: "var(--sc-pink)" }}>
            ▲ WHAT&apos;S ON THE ROADMAP
          </div>
          <h2 className="sc-heading text-3xl sm:text-4xl font-black mb-8 tracking-tight">
            A streaming arcade for creators + their crew.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                icon: Radio,
                color: "pink",
                title: "Live Streaming",
                body: "WebRTC + Cloudflare Stream for sub-second latency. OBS-compatible ingest via RTMPS / SRT / WHIP.",
              },
              {
                icon: Gamepad2,
                color: "volt",
                title: "In-Stream Multiplayer",
                body: "Chess, Checkers, Connect 4, Paddle Clash, Neon Cycles, Orb Drop and more — playable live with viewers.",
              },
              {
                icon: Coins,
                color: "orange",
                title: "Gold Coins & Tips",
                body: "Viewers purchase Gold Coins to tip streamers. 100% goes to the creator. Payouts via Stripe Connect.",
              },
              {
                icon: Sparkles,
                color: "cyan",
                title: "Sparks (Free-Play)",
                body: "Earn Sparks for watching, playing, chatting. Spend them on cosmetic borders, chat colors, emote packs.",
              },
              {
                icon: Trophy,
                color: "pink",
                title: "Channel Subscriptions",
                body: "Monthly recurring subs with sub-only chat, sub badges, streamer-set pricing, and platform-managed billing.",
              },
              {
                icon: Users,
                color: "volt",
                title: "Communities & LFG",
                body: "Friends, DMs, Party Rooms, Looking-for-Group boards, follower feeds — the streamer's room, expanded.",
              },
            ].map((f) => (
              <div key={f.title} className={`term-card ${f.color} p-5`}>
                <f.icon className="w-6 h-6 mb-3" style={{ color: `var(--sc-${f.color})` }} />
                <div className="sc-heading text-base font-bold">{f.title}</div>
                <div className="text-xs text-zinc-400 mt-2 leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing preview — for Stripe verification */}
        <div className="mb-16" data-testid="cs-pricing">
          <div className="sc-label mb-4" style={{ color: "var(--sc-orange)" }}>
            ◆ PRODUCTS AT LAUNCH
          </div>
          <h2 className="sc-heading text-3xl sm:text-4xl font-black mb-6 tracking-tight">
            What you&apos;ll be able to purchase.
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="term-card orange p-6">
              <div className="sc-label mb-2" style={{ color: "var(--sc-orange)" }}>GOLD COIN PACKS</div>
              <div className="sc-heading text-xl font-bold mb-3">Tip your favorite streamers</div>
              <ul className="text-sm text-zinc-400 space-y-1.5 sc-mono">
                <li>$4.99 · 500 Gold Coins</li>
                <li>$9.99 · 1,100 Gold Coins</li>
                <li>$19.99 · 2,400 Gold Coins</li>
                <li>$49.99 · 6,500 Gold Coins</li>
              </ul>
              <div className="text-[11px] text-zinc-500 mt-4">
                100% of tips flow to the streamer after Stripe processing fees. No cash-out for viewers.
              </div>
            </div>
            <div className="term-card pink p-6">
              <div className="sc-label mb-2" style={{ color: "var(--sc-pink)" }}>CHANNEL SUBSCRIPTIONS</div>
              <div className="sc-heading text-xl font-bold mb-3">Support creators monthly</div>
              <ul className="text-sm text-zinc-400 space-y-1.5 sc-mono">
                <li>$4.99 – $24.99 / month</li>
                <li>Price set by each streamer</li>
                <li>Sub badges, sub-only chat &amp; emotes</li>
                <li>Cancel anytime from your dashboard</li>
              </ul>
              <div className="text-[11px] text-zinc-500 mt-4">
                Recurring subscriptions billed through Stripe. Full refund policy at{" "}
                <Link to="/refunds" className="underline hover:text-white">/refunds</Link>.
              </div>
            </div>
          </div>
          <div className="sc-mono text-[11px] text-zinc-500 mt-4">
            All purchases are disabled until public launch. Prices shown are indicative and may adjust before launch.
          </div>
        </div>

        {/* Why MystiCast — value proposition + competitor comparison */}
        <WhyMystiCastSection />

        {/* Contact block */}
        <div className="mb-16 term-card p-8" data-testid="cs-contact">
          <div className="sc-label mb-4" style={{ color: "var(--sc-cyan)" }}>◈ GET IN TOUCH</div>
          <h2 className="sc-heading text-3xl sm:text-4xl font-black mb-4 tracking-tight">
            Questions? Partnerships? Press?
          </h2>
          <p className="text-sm text-zinc-400 mb-6 max-w-2xl">
            We&apos;re a small independent team gearing up for launch. Reach out on the form below or drop us an email —
            we read every message.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="sc-btn text-base flex items-center gap-2"
              data-testid="cs-contact-btn"
            >
              <MessageSquare className="w-4 h-4" /> Contact Form
            </Link>
            <a
              href="mailto:support@mysticast.tv"
              className="sc-btn-ghost text-base flex items-center gap-2"
              data-testid="cs-email-btn"
            >
              <Mail className="w-4 h-4" /> support@mysticast.tv
            </a>
          </div>

          {/* Single support address — additional department mailboxes will
              be added post-launch when they're actually provisioned. */}
          <div className="mt-6 sc-mono text-[11px] text-zinc-500">
            All inquiries: <a href="mailto:support@mysticast.tv" className="text-zinc-300 hover:text-white transition">support@mysticast.tv</a>
          </div>
        </div>

        {/* Trust / legal quick-links */}
        <div className="border-t border-white/10 pt-8" data-testid="cs-legal">
          <div className="sc-label mb-4" style={{ color: "var(--sc-volt)" }}>◤ TRUST &amp; SAFETY</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              ["/terms", "Terms of Service", Zap],
              ["/privacy", "Privacy Policy", ShieldCheck],
              ["/refunds", "Refund Policy", Coins],
              ["/cookies", "Cookie Policy", Rocket],
              ["/safety", "Community Safety", Users],
              ["/dmca", "DMCA / Copyright", ShieldCheck],
              ["/about", "About MystiCast", Radio],
              ["/contact", "Contact Us", MessageSquare],
            ].map(([to, label, Icon]) => (
              <Link
                key={to}
                to={to}
                className="term-card p-3 flex items-center gap-3 hover:brightness-125 transition"
                data-testid={`cs-legal-${to.replace("/", "")}`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" style={{ color: "var(--sc-volt)" }} />
                <span className="sc-mono text-xs text-zinc-300">{label}</span>
              </Link>
            ))}
          </div>
          <div className="sc-mono text-[11px] text-zinc-600 mt-8">
            MystiCast is an 18+ platform. All streamers must complete Stripe Identity verification (government-issued ID + selfie) before going live or receiving payouts.
            Gold Coins and Sparks are not gambling instruments and cannot be exchanged for cash by viewers.
          </div>

          {/* Staff access is intentionally NOT rendered as a visible control.
              Authorized staff open the login modal via keyboard shortcut only:
              Ctrl+Shift+L (or Cmd+Shift+L on macOS). Handler is bound in the
              parent component's useEffect. This keeps Stripe reviewers, casual
              visitors, and search crawlers from ever seeing a way in. */}
          <div className="mt-12 pt-6 border-t border-white/5 text-[10px] text-zinc-700">
            <span className="sc-mono">© 2026 MystiCast · Built for creators, by creators.</span>
          </div>
        </div>
      </div>

      {staffOpen && <StaffLoginModal onClose={() => setStaffOpen(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Staff login modal — admin-only bypass for the Coming Soon page.
//
// Uses the existing /api/auth/login endpoint. After successful auth we check
// `user.is_admin === true`. Non-admins are refused (and immediately logged
// out) so a leaked regular account never unlocks the site.
// ---------------------------------------------------------------------------
function StaffLoginModal({ onClose }) {
  const { login, logout } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setErr("");
    setSubmitting(true);
    try {
      const u = await login(identifier.trim(), password);
      if (!u || !(u.is_admin || u.is_demo)) {
        logout();
        setErr("This account is not authorized for staff access.");
        setSubmitting(false);
        return;
      }
      if (u.is_admin) {
        grantAdminUnlock();
        toast.success(`Welcome back, ${u.username}. Full app unlocked.`);
      } else {
        grantDemoUnlock();
        toast.success("Demo preview unlocked. Enjoy the walkthrough.");
      }
      // Hard-reload so the prelaunch gate re-evaluates and the whole app
      // renders in normal (non-prelaunch) mode.
      window.location.assign("/");
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Invalid credentials";
      setErr(typeof msg === "string" ? msg : "Login failed");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="cs-staff-modal"
    >
      <div
        className="term-card cyan max-w-md w-full p-6 sm:p-8 relative"
        style={{ background: "rgba(10,10,12,0.98)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-zinc-500 hover:text-white hover:bg-white/10 transition"
          data-testid="cs-staff-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="sc-label mb-3" style={{ color: "var(--sc-cyan)" }}>
          ◈ RESTRICTED · STAFF ACCESS
        </div>
        <h3 className="sc-heading text-2xl font-black mb-2">Sign in to continue</h3>
        <p className="text-xs text-zinc-500 mb-6 leading-relaxed">
          MystiCast is not yet open to the public. Only authorized staff accounts can preview the full application.
        </p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="sc-label text-[10px] block mb-1.5" style={{ color: "var(--sc-cyan)" }}>
              USERNAME OR EMAIL
            </label>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoFocus
              autoComplete="username"
              required
              data-testid="cs-staff-identifier"
              className="w-full px-4 py-2.5 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none focus:ring-1 focus:ring-[color:var(--sc-cyan)]/40 rounded sc-mono text-sm text-zinc-100 placeholder:text-zinc-600"
              placeholder="admin username"
            />
          </div>
          <div>
            <label className="sc-label text-[10px] block mb-1.5" style={{ color: "var(--sc-cyan)" }}>
              PASSWORD
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="cs-staff-password"
              className="w-full px-4 py-2.5 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none focus:ring-1 focus:ring-[color:var(--sc-cyan)]/40 rounded sc-mono text-sm text-zinc-100 placeholder:text-zinc-600"
              placeholder="••••••••"
            />
          </div>

          {err && (
            <div
              className="text-xs px-3 py-2 rounded border border-[color:var(--sc-pink)]/40 bg-[color:var(--sc-pink)]/5 text-[color:var(--sc-pink)]"
              data-testid="cs-staff-error"
            >
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="cs-staff-submit"
            className="w-full sc-btn text-base flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" /> Unlock App
              </>
            )}
          </button>
        </form>

        <div className="sc-mono text-[10px] text-zinc-600 mt-5 leading-relaxed">
          Login attempts are rate-limited. Only accounts with the admin flag can bypass the pre-launch gate.
        </div>
      </div>
    </div>
  );
}


/* ==========================================================================
 * WhyMystiCastSection — value pillars + competitor comparison table
 * ========================================================================== */

const PILLARS = [
  { label: "KEEP MORE",       sub: "70% of subs · 100% of tips",             icon: DollarSign, color: "cyan"   },
  { label: "PLAY TOGETHER",   sub: "Built-in games for you & your community", icon: Gamepad2,  color: "pink"   },
  { label: "BUILD COMMUNITY", sub: "Social feed, groups, chat & more",       icon: Users,     color: "volt"   },
  { label: "ALL-IN-ONE",      sub: "Everything you need in one platform",    icon: Zap,       color: "orange" },
];

const COMPARE_ROWS = [
  { icon: Radio,          feature: "Live Streaming",              mysti: "Full Feature",     twitch: "Full Feature", kick: "Full Feature", discord: "Limited",   patreon: "Full Feature", youtube: "Full Feature" },
  { icon: Users,          feature: "Streamer Profiles",           mysti: "Advanced",         twitch: "Basic",        kick: "Basic",        discord: "None",      patreon: "Basic",        youtube: "Basic" },
  { icon: MessageSquare,  feature: "Social Feed",                 mysti: "Full Social Feed", twitch: "Limited",      kick: "None",         discord: "Limited",   patreon: "Posts",        youtube: "Community Tab" },
  { icon: Users,          feature: "Community Hub",               mysti: "Built-in",         twitch: "None",         kick: "None",         discord: "Yes",       patreon: "Partial",      youtube: "Partial" },
  { icon: Trophy,         feature: "Built-in Subscriptions",      mysti: "Customizable",     twitch: "Limited",      kick: "Limited",      discord: "Server Subs", patreon: "Yes",        youtube: "Yes" },
  { icon: Coins,          feature: "Tips",                        mysti: "100% to Creator",  twitch: "External",     kick: "Yes",          discord: "None",      patreon: "None",         youtube: "External" },
  { icon: Gamepad2,       feature: "Games / Arcade",              mysti: "Built-in Arcade",  twitch: "None",         kick: "None",         discord: "Yes",       patreon: "None",         youtube: "None" },
  { icon: Sparkles,       feature: "Unified Creator Dashboard",   mysti: "All-in-One",       twitch: "Partial",      kick: "Partial",      discord: "None",      patreon: "Partial",      youtube: "Partial" },
  { icon: BarChart3,      feature: "Analytics",                   mysti: "Advanced",         twitch: "Advanced",     kick: "Advanced",     discord: "Limited",   patreon: "Limited",      youtube: "Advanced" },
  { icon: ShieldCheck,    feature: "Channel Staff / Mod Tools",   mysti: "Advanced",         twitch: "Advanced",     kick: "Advanced",     discord: "Advanced",  patreon: "Limited",      youtube: "Advanced" },
  { icon: ReceiptText,    feature: "Transaction History",         mysti: "Detailed",         twitch: "Partial",      kick: "Partial",      discord: "None",      patreon: "Partial",      youtube: "Partial" },
  { icon: Settings2,      feature: "Custom Subscription Mgmt",    mysti: "Fully Customizable", twitch: "Limited",    kick: "Limited",      discord: "None",      patreon: "Limited",      youtube: "Limited" },
  { icon: Eye,            feature: "Ad-free Platform Vision",     mysti: "Ad-free",          twitch: "None",         kick: "No",           discord: "Yes",       patreon: "Yes",          youtube: "No" },
];

function classifyCell(text, isMysti) {
  const t = text.toLowerCase();
  if (isMysti) return { tone: "wow", label: text };
  if (t === "none" || t === "no") return { tone: "no", label: text };
  if (["full feature", "yes", "advanced"].includes(t)) return { tone: "yes", label: text };
  return { tone: "partial", label: text };
}

const TONE_MAP = {
  wow:     { color: "var(--sc-cyan)", weight: "font-semibold" },
  yes:     { color: "#4ade80",        weight: "font-normal"   },
  partial: { color: "#f59e0b",        weight: "font-normal"   },
  no:      { color: "#ef4444",        weight: "font-normal"   },
};

function CompareCell({ text, isMysti }) {
  const { tone, label } = classifyCell(text, isMysti);
  const t = TONE_MAP[tone];
  return (
    <td className="px-3 py-3 text-center align-middle" style={{ background: isMysti ? "rgba(0,240,255,0.04)" : "transparent" }}>
      <span className={t.weight + " text-[12px] leading-tight"} style={{ color: t.color }}>{label}</span>
    </td>
  );
}

const COMPETITORS = [
  { key: "mysti",   label: "MYSTICAST", accent: "var(--sc-cyan)" },
  { key: "twitch",  label: "TWITCH",    accent: "#9146ff"        },
  { key: "kick",    label: "KICK",      accent: "#53fc18"        },
  { key: "discord", label: "DISCORD",   accent: "#5865f2"        },
  { key: "patreon", label: "PATREON",   accent: "#ff424d"        },
  { key: "youtube", label: "YOUTUBE",   accent: "#ff0033"        },
];

function WhyMystiCastSection() {
  return (
    <section className="mb-16" data-testid="cs-why-mysticast">
      {/* Header + pillars */}
      <div className="mb-8">
        <div className="sc-label mb-3" style={{ color: "var(--sc-pink)" }}>◇ WHY CREATORS CHOOSE</div>
        <h2 className="sc-heading text-4xl sm:text-5xl lg:text-6xl font-black mb-3 tracking-tight leading-none">
          <span className="text-glow-pink" style={{ color: "var(--sc-pink)" }}>MYSTI</span><span className="text-glow-pink" style={{ color: "var(--sc-volt)", textShadow: "0 0 12px var(--sc-volt)" }}>CAST</span>
        </h2>
        <p className="text-lg text-zinc-300 mb-1">One platform. Everything you need.</p>
        <p className="sc-mono text-xs text-zinc-500">
          <span style={{ color: "var(--sc-cyan)" }}>Stream.</span>{" "}
          <span style={{ color: "var(--sc-pink)" }}>Play.</span>{" "}
          <span style={{ color: "var(--sc-volt)" }}>Earn.</span>{" "}
          <span style={{ color: "var(--sc-orange)" }}>Build your community.</span>
        </p>
      </div>

      {/* Value pillars */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="cs-pillars">
        {PILLARS.map((p) => {
          const Icon = p.icon;
          const color = `var(--sc-${p.color})`;
          return (
            <div
              key={p.label}
              data-testid={`cs-pillar-${p.color}`}
              className="rounded-xl p-4 relative overflow-hidden"
              style={{
                background: "#0e0e13",
                border: `1px solid ${color}`,
                boxShadow: `0 0 24px -8px ${color}55`,
              }}
            >
              <Icon className="w-6 h-6 mb-2" style={{ color }} />
              <div className="sc-mono text-[11px] font-bold tracking-wider mb-1" style={{ color }}>
                {p.label}
              </div>
              <div className="text-[11px] text-zinc-400 leading-snug">{p.sub}</div>
            </div>
          );
        })}
      </div>

      {/* Comparison table */}
      <div
        className="rounded-xl overflow-hidden"
        style={{
          background: "#0e0e13",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
        data-testid="cs-compare-table"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] sm:text-xs" style={{ minWidth: 780 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                <th className="text-left px-4 py-4 sc-mono text-[11px] font-bold" style={{ color: "var(--sc-cyan)" }}>
                  FEATURES
                </th>
                {COMPETITORS.map((c) => (
                  <th
                    key={c.key}
                    className="px-3 py-4 sc-mono text-[11px] font-bold text-center"
                    style={{
                      color: c.accent,
                      background: c.key === "mysti" ? "rgba(0,240,255,0.06)" : "transparent",
                      borderLeft: c.key === "mysti" ? "1px solid var(--sc-cyan)" : "none",
                      borderRight: c.key === "mysti" ? "1px solid var(--sc-cyan)" : "none",
                    }}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row, i) => {
                const Icon = row.icon;
                return (
                  <tr
                    key={row.feature}
                    data-testid={`cs-compare-row-${i}`}
                    style={{
                      borderBottom: i < COMPARE_ROWS.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                    }}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <Icon className="w-4 h-4 text-zinc-500 shrink-0" />
                        <span className="text-zinc-200 text-[12px]">{row.feature}</span>
                      </div>
                    </td>
                    <CompareCell text={row.mysti}   isMysti={true} />
                    <CompareCell text={row.twitch}  isMysti={false} />
                    <CompareCell text={row.kick}    isMysti={false} />
                    <CompareCell text={row.discord} isMysti={false} />
                    <CompareCell text={row.patreon} isMysti={false} />
                    <CompareCell text={row.youtube} isMysti={false} />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Closing banner */}
      <div
        className="mt-6 rounded-xl p-6 relative overflow-hidden"
        style={{
          background: "#0e0e13",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* Subtle static gradient overlay — sits on top of the solid backing
            so the animated grid behind can't bleed through and hurt legibility. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "linear-gradient(135deg, rgba(255,16,83,0.10) 0%, rgba(0,240,255,0.08) 100%)",
          }}
        />
        <div className="relative grid lg:grid-cols-6 gap-6 items-center">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-1">
              <LogoMark size={40} />
              <div className="sc-heading text-lg font-black tracking-tight leading-tight">
                <span className="text-glow-pink" style={{ color: "var(--sc-pink)" }}>MYSTI</span><span className="text-glow-pink" style={{ color: "var(--sc-volt)", textShadow: "0 0 12px var(--sc-volt)" }}>CAST</span>{" "}
                <span className="text-zinc-200">BRINGS IT</span>
                <br />
                <span style={{ color: "var(--sc-volt)" }} className="text-glow-volt">ALL TOGETHER.</span>
              </div>
            </div>
          </div>
          <SmallPitch icon={Radio}      color="cyan"   title="STREAM"  body="From browser or OBS with low latency" />
          <SmallPitch icon={Gamepad2}   color="pink"   title="PLAY"    body="Join built-in games with your viewers in real time" />
          <SmallPitch icon={DollarSign} color="volt"   title="EARN"    body="Keep 70% of subscriptions & 100% of tips" />
          <SmallPitch icon={Users}      color="orange" title="BUILD"   body="Create communities, groups, and lasting connections" />
          <SmallPitch icon={TrendingUp} color="cyan"   title="GROW"    body="Powerful tools to manage, analyze, & grow your channel" />
        </div>
      </div>

      {/* Tagline */}
      <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
        <div className="sc-mono text-[11px] text-zinc-500 tracking-wider">
          <Rocket className="w-3.5 h-3.5 inline mr-2" style={{ color: "var(--sc-pink)" }} />
          ONE PLATFORM. ONE COMMUNITY.{" "}
          <span style={{ color: "var(--sc-pink)" }}>ENDLESS POSSIBILITIES.</span>
        </div>
        <div className="sc-mono text-[10px] text-zinc-600">JOIN THE FUTURE OF STREAMING</div>
      </div>
    </section>
  );
}

function SmallPitch({ icon: Icon, color, title, body }) {
  const c = `var(--sc-${color})`;
  return (
    <div>
      <Icon className="w-5 h-5 mb-1.5" style={{ color: c }} />
      <div className="sc-mono text-[11px] font-bold mb-1 tracking-wider" style={{ color: c }}>{title}</div>
      <div className="text-[11px] text-zinc-400 leading-snug">{body}</div>
    </div>
  );
}
